export type AnalyticsTimeRange = '1d' | '7d' | '30d' | '90d';

export const ANALYTICS_RANK_LIMIT = 25;

const RANGE_DAYS: Record<AnalyticsTimeRange, number> = {
  '1d': 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

export type FeedStatus = 'healthy' | 'warning' | 'unknown';

// Client-side `product_feed_failure` events are dominated by transient network
// blips and page-unload aborts (a visitor closing the tab before the initial
// `/db` fetch settles). A single such blip should not flag the feed as failing.
// A genuine feed outage suppresses almost every other first-party event, so its
// failure share is high. Only flag the feed when failures are a meaningful share
// of all events in the window.
export const FEED_FAILURE_WARNING_SHARE = 0.05;

export function classifyFeedStatus(totalEvents: number, productFeedFailures: number): FeedStatus {
  if (totalEvents <= 0) return 'unknown';
  if (productFeedFailures <= 0) return 'healthy';
  return productFeedFailures / totalEvents >= FEED_FAILURE_WARNING_SHARE ? 'warning' : 'healthy';
}

export interface AnalyticsDateRange {
  start: string;
  end: string;
}

export interface AnalyticsComparisonWindow {
  current: AnalyticsDateRange;
  previous: AnalyticsDateRange;
}

export interface AnalyticsOverview {
  start: string | null;
  end: string | null;
  totalEvents: number;
  affiliateClicks: number;
  productClicks: number;
  agentSelections: number;
  searches: number;
  zeroResultSearches: number;
  clientErrors: number;
  productFeedFailures: number;
  redirectRequests: number;
  redirectFailures: number;
  zeroResultRate: number | null;
  redirectFailureRate: number | null;
  feedStatus: FeedStatus;
}

export interface AgentSummaryRpcRow {
  agent: string | null;
  affiliate_clicks: number | string | null;
  selections: number | string | null;
}

export interface AnalyticsAgentRow {
  agent: string;
  affiliateClicks: number;
  selections: number;
  clickShare: number | null;
  selectionShare: number | null;
}

export interface ProductSummaryRpcRow {
  product_id: string | null;
  product_clicks: number | string | null;
  affiliate_clicks: number | string | null;
}

export interface ProductEnrichmentRow {
  id: string | number | null;
  name?: string | null;
  brand?: string | null;
  category?: string | null;
  'category[0]'?: string | null;
}

export interface AnalyticsProductRow {
  productId: string;
  name: string;
  brand: string;
  category: string;
  productClicks: number;
  affiliateClicks: number;
}

export interface DiscoverySummaryRpcRow {
  event_name: string | null;
  dimension: string | null;
  event_count: number | string | null;
}

export interface AnalyticsDiscoveryRow {
  dimension: string;
  count: number;
}

export interface AnalyticsDiscoverySummary {
  topSearches: AnalyticsDiscoveryRow[];
  missedDemand: AnalyticsDiscoveryRow[];
  filterUsage: AnalyticsDiscoveryRow[];
  clientErrors: AnalyticsDiscoveryRow[];
  feedFailures: AnalyticsDiscoveryRow[];
}

export interface AnalyticsDiscoverySignalRow {
  key: string;
  source: 'feed_failure' | 'client_error';
  dimension: string;
  count: number;
}

export interface ReliabilitySummaryRpcRow {
  metric_key: string | null;
  dimension: string | null;
  event_count: number | string | null;
}

export interface AnalyticsReliabilityRow {
  dimension: string;
  count: number;
}

export interface AnalyticsReliabilitySummary {
  requestsByAgent: AnalyticsReliabilityRow[];
  requestsByMarketplace: AnalyticsReliabilityRow[];
  failuresByAgent: AnalyticsReliabilityRow[];
  failuresByMarketplace: AnalyticsReliabilityRow[];
  failuresByStatus: AnalyticsReliabilityRow[];
  failuresByStage: AnalyticsReliabilityRow[];
}

export interface AnalyticsMetricDelta {
  previousValue: number;
  absoluteChange: number;
  percentChange: number | null;
  hasPriorBaseline: boolean;
}

export function getAnalyticsDateRange(range: AnalyticsTimeRange, now = new Date()): AnalyticsDateRange {
  const end = new Date(now);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - RANGE_DAYS[range]);

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

export function getAnalyticsComparisonRanges(range: AnalyticsTimeRange, now = new Date()): AnalyticsComparisonWindow {
  const current = getAnalyticsDateRange(range, now);
  const currentStartMs = new Date(current.start).getTime();
  const currentEndMs = new Date(current.end).getTime();
  const durationMs = Math.max(0, currentEndMs - currentStartMs);

  return {
    current,
    previous: {
      start: new Date(currentStartMs - durationMs).toISOString(),
      end: new Date(currentStartMs).toISOString(),
    },
  };
}

export function calculateRate(numerator: number | string | null | undefined, denominator: number | string | null | undefined): number | null {
  const top = toNumber(numerator);
  const bottom = toNumber(denominator);

  if (bottom <= 0) return null;

  return Math.round((top / bottom) * 1000) / 10;
}

export function compareMetricValues(current: number | string | null | undefined, previous: number | string | null | undefined): AnalyticsMetricDelta {
  const currentValue = toNumber(current);
  const previousValue = toNumber(previous);
  const absoluteChange = currentValue - previousValue;

  return {
    previousValue,
    absoluteChange,
    percentChange: previousValue > 0 ? Math.round(((absoluteChange / previousValue) * 1000)) / 10 : null,
    hasPriorBaseline: previousValue > 0,
  };
}

export function normalizeOverview(raw: Record<string, unknown> | null | undefined): AnalyticsOverview {
  const totalEvents = toNumber(raw?.total_events);
  const searches = toNumber(raw?.searches);
  const zeroResultSearches = toNumber(raw?.zero_result_searches);
  const productFeedFailures = toNumber(raw?.product_feed_failures);

  return {
    start: typeof raw?.start === 'string' ? raw.start : null,
    end: typeof raw?.end === 'string' ? raw.end : null,
    totalEvents,
    affiliateClicks: toNumber(raw?.affiliate_clicks),
    productClicks: toNumber(raw?.product_clicks),
    agentSelections: toNumber(raw?.agent_selections),
    searches,
    zeroResultSearches,
    clientErrors: toNumber(raw?.client_errors),
    productFeedFailures,
    redirectRequests: toNumber(raw?.redirect_requests),
    redirectFailures: toNumber(raw?.redirect_failures),
    zeroResultRate: calculateRate(zeroResultSearches, searches),
    redirectFailureRate: calculateRate(raw?.redirect_failures, raw?.redirect_requests),
    feedStatus: classifyFeedStatus(totalEvents, productFeedFailures),
  };
}

export function buildAgentRows(rows: AgentSummaryRpcRow[], limit = ANALYTICS_RANK_LIMIT): AnalyticsAgentRow[] {
  const normalized = rows.map((row) => ({
    agent: row.agent || 'unknown',
    affiliateClicks: toNumber(row.affiliate_clicks),
    selections: toNumber(row.selections),
  }));
  const totalClicks = normalized.reduce((sum, row) => sum + row.affiliateClicks, 0);
  const totalSelections = normalized.reduce((sum, row) => sum + row.selections, 0);

  return normalized
    .sort((a, b) => b.affiliateClicks - a.affiliateClicks || b.selections - a.selections || a.agent.localeCompare(b.agent))
    .slice(0, Math.min(limit, ANALYTICS_RANK_LIMIT))
    .map((row) => {
      return {
        ...row,
        clickShare: calculateRate(row.affiliateClicks, totalClicks),
        selectionShare: calculateRate(row.selections, totalSelections),
      };
    });
}

export function buildProductRows(
  rows: ProductSummaryRpcRow[],
  products: ProductEnrichmentRow[],
  limit = ANALYTICS_RANK_LIMIT,
): AnalyticsProductRow[] {
  const productMap = new Map(products.map((product) => [String(product.id), product]));

  return rows
    .filter((row) => Boolean(row.product_id))
    .map((row) => ({
      productId: String(row.product_id),
      productClicks: toNumber(row.product_clicks),
      affiliateClicks: toNumber(row.affiliate_clicks),
    }))
    .sort((a, b) => b.productClicks - a.productClicks || b.affiliateClicks - a.affiliateClicks || a.productId.localeCompare(b.productId))
    .slice(0, Math.min(limit, ANALYTICS_RANK_LIMIT))
    .map((row) => {
      const product = productMap.get(row.productId);
      return {
        ...row,
        name: product?.name || row.productId,
        brand: product?.brand || 'Unknown',
        category: product?.['category[0]'] || product?.category || 'Uncategorized',
      };
    });
}

export function buildDiscoverySummary(rows: DiscoverySummaryRpcRow[], limit = ANALYTICS_RANK_LIMIT): AnalyticsDiscoverySummary {
  return {
    topSearches: discoveryRowsFor(rows, 'search', limit),
    missedDemand: discoveryRowsFor(rows, 'zero_result_search', limit),
    filterUsage: discoveryRowsFor(rows, 'filter_applied', limit),
    clientErrors: discoveryRowsFor(rows, 'client_error', limit),
    feedFailures: discoveryRowsFor(rows, 'product_feed_failure', limit),
  };
}

export function hasDiscoveryContent(summary: AnalyticsDiscoverySummary | null | undefined): boolean {
  if (!summary) return false;

  return Boolean(
    summary.topSearches.length ||
      summary.missedDemand.length ||
      summary.filterUsage.length ||
      summary.clientErrors.length ||
      summary.feedFailures.length,
  );
}

export function buildDiscoverySignalRows(
  summary: AnalyticsDiscoverySummary,
  limit = ANALYTICS_RANK_LIMIT,
): AnalyticsDiscoverySignalRow[] {
  return [
    ...summary.feedFailures.map((row) => ({
      key: `feed:${row.dimension}`,
      source: 'feed_failure' as const,
      dimension: row.dimension,
      count: row.count,
    })),
    ...summary.clientErrors.map((row) => ({
      key: `client:${row.dimension}`,
      source: 'client_error' as const,
      dimension: row.dimension,
      count: row.count,
    })),
  ]
    .sort((a, b) => b.count - a.count || a.source.localeCompare(b.source) || a.dimension.localeCompare(b.dimension))
    .slice(0, Math.min(limit, ANALYTICS_RANK_LIMIT));
}

export function buildReliabilitySummary(
  rows: ReliabilitySummaryRpcRow[],
  limit = ANALYTICS_RANK_LIMIT,
): AnalyticsReliabilitySummary {
  return {
    requestsByAgent: reliabilityRowsFor(rows, 'redirect_requests_by_agent', limit),
    requestsByMarketplace: reliabilityRowsFor(rows, 'redirect_requests_by_marketplace', limit),
    failuresByAgent: reliabilityRowsFor(rows, 'redirect_failures_by_agent', limit),
    failuresByMarketplace: reliabilityRowsFor(rows, 'redirect_failures_by_marketplace', limit),
    failuresByStatus: reliabilityRowsFor(rows, 'redirect_failures_by_status', limit),
    failuresByStage: reliabilityRowsFor(rows, 'redirect_failures_by_stage', limit),
  };
}

export function hasReliabilityContent(summary: AnalyticsReliabilitySummary | null | undefined): boolean {
  if (!summary) return false;

  return Boolean(
    summary.requestsByAgent.length ||
      summary.requestsByMarketplace.length ||
      summary.failuresByAgent.length ||
      summary.failuresByMarketplace.length ||
      summary.failuresByStatus.length ||
      summary.failuresByStage.length,
  );
}

export function toNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function discoveryRowsFor(rows: DiscoverySummaryRpcRow[], eventName: string, limit: number): AnalyticsDiscoveryRow[] {
  return rows
    .filter((row) => row.event_name === eventName)
    .map((row) => ({
      dimension: row.dimension || 'unknown',
      count: toNumber(row.event_count),
    }))
    .sort((a, b) => b.count - a.count || a.dimension.localeCompare(b.dimension))
    .slice(0, Math.min(limit, ANALYTICS_RANK_LIMIT));
}

function reliabilityRowsFor(rows: ReliabilitySummaryRpcRow[], metricKey: string, limit: number): AnalyticsReliabilityRow[] {
  return rows
    .filter((row) => row.metric_key === metricKey)
    .map((row) => ({
      dimension: row.dimension || 'unknown',
      count: toNumber(row.event_count),
    }))
    .sort((a, b) => b.count - a.count || a.dimension.localeCompare(b.dimension))
    .slice(0, Math.min(limit, ANALYTICS_RANK_LIMIT));
}
